import api from './api';

export interface OrderFinancialRecord {
    id: string;
    orderId: string;
    vendorId: string;
    totalRevenue: number;
    courierFee: number;
    vendorCommission: number;
    totalExpenses: number;
    netProfit: number;
    status: string;
    reportedDate: Date | any;
}

export interface VendorFinancialsSummary {
    vendorId: string;
    vendorName: string;
    totalRevenue: number;
    totalExpenses: number;
    netProfit: number;
    orderCount: number;
    courierDeliveryFee: number;
    averageOrderValue: number;
    dateRange: {
        startDate: Date;
        endDate: Date;
    };
}

export interface CourierEarningsSummary {
    courierId: string;
    courierName: string;
    totalEarned: number;
    completedDeliveries: number;
    averageEarnings: number;
    dateRange: {
        startDate: Date;
        endDate: Date;
    };
    deliveryHistory: Array<{
        orderId: string;
        amount: number;
        completedAt: Date | any;
    }>;
}

export const accountingApi = {
    // Admin: Tüm order finansal kayıtları
    async getOrderFinancials(params?: {
        dateStart?: string;
        dateEnd?: string;
        vendorId?: string;
        status?: string;
        limit?: number;
        offset?: number;
    }): Promise<{
        orders: OrderFinancialRecord[];
        totals: {
            totalRevenue: number;
            totalCourierFees: number;
            totalVendorCommissions: number;
            totalProfit: number;
        };
        hasMore: boolean;
        total: number;
    }> {
        try {
            console.log('📡 Calling GET /admin/accounting/orders with params:', params);
            const response = await api.get('/admin/accounting/orders', { params });
            console.log('📥 Response:', response.data);
            return response.data;
        } catch (error: any) {
            console.error('❌ Accounting API Error:', error.response || error);
            throw new Error(
                error.response?.data?.error || 'Finansal veriler yüklenemedi'
            );
        }
    },

    // Admin: Vendor finansal özeti
    async getVendorFinancialsSummary(
        vendorId: string,
        params?: {
            dateStart?: string;
            dateEnd?: string;
        }
    ): Promise<VendorFinancialsSummary> {
        try {
            const response = await api.get(`/admin/accounting/vendors/${vendorId}`, { params });
            return response.data;
        } catch (error: any) {
            console.error('Error fetching vendor financials:', error);
            throw new Error(
                error.response?.data?.error || 'Vendor finansalları yüklenemedi'
            );
        }
    },

    // Admin: Kurye kazanç detayları
    async getCourierEarnings(
        courierId: string,
        params?: {
            dateStart?: string;
            dateEnd?: string;
        }
    ): Promise<CourierEarningsSummary> {
        try {
            const response = await api.get(`/admin/accounting/couriers/${courierId}`, { params });
            return response.data;
        } catch (error: any) {
            console.error('Error fetching courier earnings:', error);
            throw new Error(
                error.response?.data?.error || 'Kurye kazançları yüklenemedi'
            );
        }
    },

    // Admin: Genel finansal rapor
    async getFinancialReport(params?: {
        dateStart?: string;
        dateEnd?: string;
        type?: 'global' | 'vendor' | 'courier';
    }): Promise<any> {
        try {
            const response = await api.get('/admin/accounting/report', { params });
            return response.data;
        } catch (error: any) {
            console.error('Error fetching financial report:', error);
            throw new Error(
                error.response?.data?.error || 'Finansal rapor yüklenemedi'
            );
        }
    },

    // Admin: Vendor courier delivery fee güncelle
    async updateVendorCourierDeliveryFee(
        vendorId: string,
        courierDeliveryFee: number
    ): Promise<{ success: boolean; courierDeliveryFee: number }> {
        try {
            const response = await api.put(
                `/admin/accounting/vendors/${vendorId}/courier-delivery-fee`,
                { courierDeliveryFee }
            );
            return response.data;
        } catch (error: any) {
            console.error('Error updating courier delivery fee:', error);
            throw new Error(
                error.response?.data?.error || 'Fee güncellenemedi'
            );
        }
    },

    // Order'a kurye atama (accounting perspective)
    async assignCourierToOrder(
        orderId: string,
        courierId: string,
        courierFee?: number
    ): Promise<{ success: boolean; orderId: string; courierId: string; courierFee: number }> {
        try {
            const response = await api.post(`/admin/accounting/orders/${orderId}/assign-courier`, {
                courierId,
                courierFee,
            });
            return response.data;
        } catch (error: any) {
            console.error('Error assigning courier to order:', error);
            throw new Error(
                error.response?.data?.error || 'Kurye ataması başarısız'
            );
        }
    },
};
